import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FileSignature, ChevronLeft, Mail } from 'lucide-react';

const DPAPage = () => {
  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <Helmet>
        <title>Data Processing Addendum - Sortio</title>
        <meta name="description" content="Sortio's Data Processing Addendum (DPA): GDPR Article 28 processor terms, sub-processing, transfers (SCCs Module 2), security, and breach notification." />
      </Helmet>

      <div className="absolute top-0 inset-x-0 h-[500px] bg-gradient-to-b from-apple-700/10 to-transparent"></div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-12 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-16 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <FileSignature className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            Data Processing <span className="apple-gradient-text">Addendum</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-2xl">
            Standard processor terms under GDPR Article 28. This page reproduces our DPA template for review; a signed copy is available on request.
          </p>
        </div>

        <div className="apple-panel p-8 md:p-12 rounded-apple backdrop-blur-sm mb-12">
          <div className="space-y-10 text-apple-300 leading-relaxed">
            <p className="text-sm text-apple-400"><em>Template version: 16 May 2026</em></p>

            <div className="p-4 rounded-lg bg-sortio-primary/10 border border-sortio-primary/30 flex items-start gap-3">
              <Mail className="w-5 h-5 text-sortio-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
              <div className="text-sm">
                <p className="text-apple-100 font-medium">Request a signed copy</p>
                <p className="text-apple-300 mt-1">
                  Email <a href="mailto:marcus@getsortio.com?subject=DPA%20request" className="text-sortio-primary hover:text-sortio-primary/80">marcus@getsortio.com</a> with your legal entity name, registered address, and the Sortio account email. We countersign within 5 business days.
                </p>
              </div>
            </div>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">1. Definitions</h2>
              <p>
                "Controller", "Processor", "Data Subject", "Personal Data", "Processing", and "Special Categories" have the meanings given in the General Data Protection Regulation 2016/679 ("GDPR"). "UK GDPR" means the GDPR as retained in UK law. "Customer" means the entity entering into a subscription agreement with CMG Labs LLC ("Sortio", "us", "we"). "Customer Data" means Personal Data that Customer provides to Sortio for Processing.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">2. Scope and roles</h2>
              <p>
                Customer is the Controller of Customer Data. Sortio is the Processor and Processes Customer Data only on documented instructions from Customer, which are reflected in the Sortio subscription agreement, this DPA, and Customer's use of the product features.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">3. Processor obligations</h2>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Process Customer Data only on documented instructions from Customer.</li>
                <li>Ensure that personnel authorized to Process Customer Data are bound by confidentiality.</li>
                <li>Implement appropriate technical and organizational measures (see Annex 1).</li>
                <li>Assist Customer with Data Subject requests, data protection impact assessments, and consultations with supervisory authorities.</li>
                <li>At Customer's choice, delete or return Customer Data after the end of the provision of services, except where retention is required by law.</li>
                <li>Make available all information necessary to demonstrate compliance with Article 28 GDPR.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">4. Sub-processing</h2>
              <p>
                Customer authorizes Sortio to engage the sub-processors listed on our public <Link to="/sub-processors" className="text-sortio-primary hover:text-sortio-primary/80">Sub-processors</Link> page. Sortio gives Customer at least 30 days' notice of any new or replacement sub-processor through the Sub-processors page or by email. Customer may object on reasonable data protection grounds; if a resolution cannot be reached, Customer may terminate the affected service for the remainder of the term and receive a pro-rata refund of prepaid fees. Sortio imposes data protection obligations on each sub-processor that are no less protective than those in this DPA.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">5. International transfers</h2>
              <p>
                Customer Data is Processed in the United States and, where indicated on the Sub-processors page, in other regions. For transfers of Personal Data of EEA, Swiss, or UK Data Subjects to the United States, the parties incorporate the Standard Contractual Clauses (Commission Implementing Decision (EU) 2021/914), Module Two (Controller-to-Processor), with the optional docking clause and the option for the importer to be subject to GDPR. The UK International Data Transfer Addendum applies where the UK GDPR governs. Where a sub-processor self-certifies under the EU-US Data Privacy Framework or its UK / Swiss extensions, that mechanism applies in addition to the SCCs.
              </p>
              <p>
                Sortio has carried out a transfer impact assessment and applies the supplementary measures described in Annex 1 to mitigate risks identified in Schrems II.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">6. Security</h2>
              <p>
                Sortio implements the technical and organizational measures described in Annex 1, including encryption in transit (TLS 1.2 or higher), encryption at rest (default disk-level encryption on managed providers including Google Cloud SQL and Google Cloud Storage), role-based access controls with least-privilege defaults, audit logging on production systems, secret rotation, and code-signed release artifacts for the desktop application.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">7. Cooperation with Data Subjects and authorities</h2>
              <p>
                Sortio assists Customer in responding to Data Subject requests under Articles 12 to 22 of the GDPR. Where a Data Subject contacts Sortio directly with a request relating to Customer Data, Sortio forwards the request to Customer and does not respond substantively without Customer's instruction, except as required by law. Sortio cooperates with reasonable requests from competent supervisory authorities.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">8. Personal data breach notification</h2>
              <p>
                Sortio notifies Customer without undue delay and in any event within 72 hours of becoming aware of a Personal Data Breach affecting Customer Data, providing the information required by Article 33(3) GDPR to the extent then known and updating Customer as further information becomes available. Notifications do not constitute an admission of fault or liability.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">9. Audit rights</h2>
              <p>
                Once per year, on at least 30 days' written notice and during business hours, Customer (or a mutually agreed independent third-party auditor bound by confidentiality) may audit Sortio's compliance with this DPA. To minimize disruption, Sortio may satisfy audit requests by providing recent third-party audit reports (such as the SOC 2 report once Sortio's planned 2026 audit completes), a completed CAIQ, or a documented response to a written audit questionnaire.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">10. Termination and deletion</h2>
              <p>
                On termination of the subscription agreement, and on Customer's request, Sortio deletes or returns all Customer Data within 30 days, except for backups and audit logs subject to standard retention windows. Where retention is required by law (for example, tax records), Sortio continues to apply the technical and organizational measures of this DPA to retained data until deletion.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">11. Liability</h2>
              <p>
                Each party's liability arising out of or in connection with this DPA is subject to the limitations and exclusions of liability set out in the subscription agreement. Nothing in this DPA limits any liability that cannot be limited under applicable law (including direct liability of either party to Data Subjects under Article 82 GDPR).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">12. General</h2>
              <p>
                In the event of a conflict between this DPA and the subscription agreement, this DPA prevails to the extent the conflict concerns the Processing of Personal Data. In the event of a conflict between this DPA and the Standard Contractual Clauses, the Standard Contractual Clauses prevail.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Annex 1: Technical and organizational measures (summary)</h2>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Encryption in transit using TLS 1.2 or higher between client and server, and between Sortio and all sub-processors.</li>
                <li>Encryption at rest on managed data stores (Google Cloud SQL, Google Cloud Storage and GCS buckets).</li>
                <li>Role-based access control with least-privilege defaults; production access limited to a small named list and reviewed at least quarterly.</li>
                <li>Audit logging on production systems; secret rotation through Google Secret Manager.</li>
                <li>Code-signed release artifacts for the desktop application.</li>
                <li>Vulnerability scanning on first-party code and dependency upgrades on a regular cadence.</li>
                <li>Documented incident response process and breach notification path.</li>
                <li>Annual review of the supplementary measures described above in light of evolving Schrems II guidance.</li>
              </ul>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Annex 2: Categories of data and Data Subjects</h2>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li><strong>Data Subjects:</strong> Customer's employees, contractors, and any individuals whose Personal Data appears in files or filenames that Customer chooses to Process through Sortio.</li>
                <li><strong>Categories of Personal Data:</strong> identifiers (name, email, account identifiers), commercial information, electronic network activity, content of files (only if Customer enables content-sorting features).</li>
                <li><strong>Special categories:</strong> Sortio is not designed to Process special categories of data (GDPR Article 9) or data subject to HIPAA, FERPA, ITAR, or PCI DSS. Customer must not submit such data unless the parties agree separately in writing.</li>
                <li><strong>Frequency:</strong> Continuous, for the duration of the subscription.</li>
                <li><strong>Nature of Processing:</strong> Storage, organization, classification, transmission to LLM sub-processors for inference, retrieval.</li>
              </ul>
            </section>

            <p className="text-sm text-apple-400">
              <em>This page reproduces our DPA template. Where Customer and Sortio sign a separate DPA, the signed version governs.</em>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DPAPage;
